import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import {
  BookOpen, CheckCircle, Circle, ExternalLink, ChevronDown, ChevronUp,
  Building2, Plus, Loader2, Shield, Hash, MapPin, Phone, Mail, Briefcase
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── GUIDE STEP CARD ───
const GuideStepCard = ({ step, progress, onToggle }) => {
  const [expanded, setExpanded] = useState(false);
  const isCompleted = progress?.[step.id] || false;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`border transition-colors ${
        isCompleted ? "bg-emerald-500/5 border-emerald-500/20" : "bg-[#0a0f1a] border-white/5"
      }`}
    >
      <div
        className="flex items-center gap-4 p-4 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
        data-testid={`guide-step-${step.number}`}
      >
        <div className={`w-10 h-10 flex items-center justify-center flex-shrink-0 font-bold text-lg ${
          isCompleted ? "bg-emerald-500 text-white" : "bg-[#f59e0b]/10 text-[#f59e0b]"
        }`}>
          {isCompleted ? <CheckCircle className="w-5 h-5" /> : step.number}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className={`font-bold text-sm ${isCompleted ? "text-emerald-400" : "text-white"}`}>{step.title}</h4>
          <p className="text-[#94a3b8] text-xs truncate">{step.description}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); onToggle(step.id, !isCompleted); }}
            className={`text-xs px-3 py-1 font-semibold transition-colors ${
              isCompleted
                ? "bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20"
                : "bg-white/5 text-[#94a3b8] hover:bg-white/10"
            }`}
            data-testid={`guide-toggle-${step.number}`}
          >
            {isCompleted ? "Completado" : "Marcar listo"}
          </button>
          {expanded ? <ChevronUp className="w-4 h-4 text-[#94a3b8]" /> : <ChevronDown className="w-4 h-4 text-[#94a3b8]" />}
        </div>
      </div>

      {expanded && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          className="px-4 pb-4 border-t border-white/5"
        >
          <ul className="mt-3 space-y-2">
            {step.details?.map((detail, i) => (
              <li key={i} className="flex items-start gap-2 text-[#94a3b8] text-sm">
                <span className="text-[#f59e0b] mt-1">&#8226;</span>
                <span>{detail}</span>
              </li>
            ))}
          </ul>
          {step.link && (
            <a
              href={step.link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-3 text-[#f59e0b] text-sm font-medium hover:underline"
            >
              <ExternalLink className="w-3.5 h-3.5" /> {step.link_text}
            </a>
          )}
        </motion.div>
      )}
    </motion.div>
  );
};

// ─── REGISTER FISCAL CLIENT FORM ───
const RegisterFiscalForm = ({ onCreated, onCancel }) => {
  const [form, setForm] = useState({
    rnc: "", business_name: "", trade_name: "", address: "", phone: "", email: "", economic_activity: ""
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.rnc || !form.business_name) { toast.error("RNC y razon social son requeridos"); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/invoicing/fiscal-clients`, form);
      toast.success("Empresa registrada exitosamente");
      onCreated(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al registrar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-[#0a0f1a] border border-white/5 p-5 space-y-4" data-testid="register-fiscal-form">
      <p className="text-white font-bold text-sm">Registrar Empresa para Facturacion</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-[#94a3b8] text-xs mb-1.5">RNC *</label>
          <div className="relative">
            <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94a3b8]" />
            <input value={form.rnc} onChange={(e) => setForm({ ...form, rnc: e.target.value })} placeholder="123456789" required className="w-full bg-[#02040a] border border-white/10 pl-10 pr-4 py-2.5 text-white text-sm focus:border-[#f59e0b] focus:outline-none" data-testid="fiscal-rnc" />
          </div>
        </div>
        <div>
          <label className="block text-[#94a3b8] text-xs mb-1.5">Razon Social *</label>
          <div className="relative">
            <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94a3b8]" />
            <input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} placeholder="Empresa SRL" required className="w-full bg-[#02040a] border border-white/10 pl-10 pr-4 py-2.5 text-white text-sm focus:border-[#f59e0b] focus:outline-none" data-testid="fiscal-business-name" />
          </div>
        </div>
        <div>
          <label className="block text-[#94a3b8] text-xs mb-1.5">Nombre Comercial</label>
          <input value={form.trade_name} onChange={(e) => setForm({ ...form, trade_name: e.target.value })} placeholder="Nombre comercial" className="w-full bg-[#02040a] border border-white/10 px-4 py-2.5 text-white text-sm focus:border-[#f59e0b] focus:outline-none" />
        </div>
        <div>
          <label className="block text-[#94a3b8] text-xs mb-1.5">Actividad Economica</label>
          <div className="relative">
            <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94a3b8]" />
            <input value={form.economic_activity} onChange={(e) => setForm({ ...form, economic_activity: e.target.value })} placeholder="Ej: Venta al por menor" className="w-full bg-[#02040a] border border-white/10 pl-10 pr-4 py-2.5 text-white text-sm focus:border-[#f59e0b] focus:outline-none" />
          </div>
        </div>
        <div>
          <label className="block text-[#94a3b8] text-xs mb-1.5">Direccion</label>
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94a3b8]" />
            <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Calle, Ciudad" className="w-full bg-[#02040a] border border-white/10 pl-10 pr-4 py-2.5 text-white text-sm focus:border-[#f59e0b] focus:outline-none" />
          </div>
        </div>
        <div>
          <label className="block text-[#94a3b8] text-xs mb-1.5">Telefono</label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94a3b8]" />
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="809-000-0000" className="w-full bg-[#02040a] border border-white/10 pl-10 pr-4 py-2.5 text-white text-sm focus:border-[#f59e0b] focus:outline-none" />
          </div>
        </div>
      </div>
      <div className="flex gap-3">
        <motion.button
          type="submit"
          disabled={saving}
          className="bg-[#f59e0b] text-black text-sm font-bold px-6 py-2.5 flex items-center gap-2 hover:bg-[#d97706] disabled:opacity-50"
          whileHover={{ boxShadow: "0 0 20px rgba(245,158,11,0.3)" }}
          data-testid="fiscal-submit-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Registrar Empresa
        </motion.button>
        <button type="button" onClick={onCancel} className="text-[#94a3b8] hover:text-white text-sm px-4">Cancelar</button>
      </div>
    </form>
  );
};

// ─── MAIN GUIDE TAB ───
const EcfGuideTab = () => {
  const [guide, setGuide] = useState(null);
  const [fiscalClients, setFiscalClients] = useState([]);
  const [selectedFc, setSelectedFc] = useState(null);
  const [showRegister, setShowRegister] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [guideRes, fcRes] = await Promise.all([
        axios.get(`${API}/invoicing/guide`),
        axios.get(`${API}/invoicing/fiscal-clients`),
      ]);
      setGuide(guideRes.data);
      setFiscalClients(fcRes.data.fiscal_clients || []);
      if (fcRes.data.fiscal_clients?.length > 0 && !selectedFc) {
        setSelectedFc(fcRes.data.fiscal_clients[0]);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  }, [selectedFc]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleToggleStep = async (stepId, completed) => {
    if (!selectedFc) { toast.error("Registra tu empresa primero"); return; }
    try {
      const res = await axios.put(
        `${API}/invoicing/fiscal-clients/${selectedFc.id}/guide`,
        { step: stepId, completed }
      );
      setSelectedFc(res.data);
      setFiscalClients((prev) => prev.map((fc) => (fc.id === res.data.id ? res.data : fc)));
    } catch (err) {
      toast.error("Error al actualizar progreso");
    }
  };

  const progress = selectedFc?.guide_progress || {};
  const completedSteps = Object.values(progress).filter(Boolean).length;
  const totalSteps = guide?.steps?.length || 4;
  const progressPct = Math.round((completedSteps / totalSteps) * 100);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-[#f59e0b] animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="ecf-guide-tab">
      <div>
        <h2 className="text-white text-xl font-bold flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-[#f59e0b]" /> Guia de Implementacion e-CF
        </h2>
        <p className="text-[#94a3b8] text-sm">Sigue estos pasos para habilitar la facturacion electronica para tu empresa</p>
      </div>

      {/* Fiscal Client Selector / Register */}
      {fiscalClients.length === 0 && !showRegister ? (
        <div className="bg-[#0a0f1a] border border-white/5 p-6 text-center">
          <Building2 className="w-12 h-12 text-[#f59e0b]/20 mx-auto mb-3" />
          <p className="text-white font-semibold mb-2">Registra tu empresa primero</p>
          <p className="text-[#94a3b8] text-sm mb-4">Necesitas registrar los datos fiscales de tu empresa para comenzar</p>
          <button
            onClick={() => setShowRegister(true)}
            className="bg-[#f59e0b] text-black text-sm font-bold px-6 py-2.5 hover:bg-[#d97706] transition-colors"
            data-testid="register-first-company-btn"
          >
            Registrar Mi Empresa
          </button>
        </div>
      ) : showRegister ? (
        <RegisterFiscalForm
          onCreated={(fc) => { setFiscalClients([fc, ...fiscalClients]); setSelectedFc(fc); setShowRegister(false); }}
          onCancel={() => setShowRegister(false)}
        />
      ) : (
        <>
          <div className="flex items-center gap-3">
            <select
              value={selectedFc?.id || ""}
              onChange={(e) => setSelectedFc(fiscalClients.find((fc) => fc.id === e.target.value))}
              className="bg-[#0a0f1a] border border-white/10 px-4 py-2 text-white text-sm focus:border-[#f59e0b] focus:outline-none flex-1"
              data-testid="fiscal-client-selector"
            >
              {fiscalClients.map((fc) => (
                <option key={fc.id} value={fc.id}>{fc.business_name} (RNC: {fc.rnc})</option>
              ))}
            </select>
            <button
              onClick={() => setShowRegister(true)}
              className="bg-white/5 border border-white/10 text-[#94a3b8] hover:text-white p-2 transition-colors"
              data-testid="add-fiscal-btn"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>

          {/* Progress Bar */}
          <div className="bg-[#0a0f1a] border border-white/5 p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-white font-semibold text-sm">Progreso de Implementacion</span>
              <span className="text-[#f59e0b] font-bold text-sm">{progressPct}%</span>
            </div>
            <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-[#f59e0b] to-[#d97706]"
                initial={{ width: 0 }}
                animate={{ width: `${progressPct}%` }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              />
            </div>
            <p className="text-[#94a3b8] text-xs mt-2">{completedSteps} de {totalSteps} pasos completados</p>
          </div>
        </>
      )}

      {/* Guide Steps */}
      {guide?.steps && (
        <div className="space-y-3">
          {guide.steps.map((step) => (
            <GuideStepCard
              key={step.id}
              step={step}
              progress={progress}
              onToggle={handleToggleStep}
            />
          ))}
        </div>
      )}

      {/* Deadlines */}
      {guide?.deadlines && (
        <div className="bg-[#0a0f1a] border border-white/5 p-5">
          <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
            <Shield className="w-4 h-4 text-red-400" /> Fechas Limite DGII
          </h3>
          <div className="space-y-2">
            {guide.deadlines.map((d, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="text-[#94a3b8]">{d.type}</span>
                <span className="text-red-400 font-semibold">{d.date}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default EcfGuideTab;
